import { useState, useEffect } from 'react';

const QUERY = "(prefers-reduced-motion: reduce)";
/**
 * Hook to observe the user's prefers-reduced-motion setting.
 * Returns true when the user requests reduced motion.
 *
 * Ensures cross-browser compatibility with fallbacks for older Safari versions.
 */
function usePrefersReducedMotion() {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    useEffect(() => {
        // SSR guard - return early if window is not available
        if (typeof window === "undefined" || !window.matchMedia) {
            return;
        }
        const mediaQuery = window.matchMedia(QUERY);
        // Update state with current value
        const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
        updatePreference();
        // Modern browsers support addEventListener
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", updatePreference);
            return () => mediaQuery.removeEventListener("change", updatePreference);
        }
        // Fallback for older Safari versions - use try/catch for safety
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            mediaQuery.addListener(updatePreference);
            return () => {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    mediaQuery.removeListener(updatePreference);
                }
                catch (_a) {
                    // Ignore errors during cleanup
                }
            };
        }
        catch (_a) {
            // If legacy methods fail, we'll just use the initial value
        }
    }, []);
    return prefersReducedMotion;
}

export { usePrefersReducedMotion };
